import { cn } from "@habits/shared";

interface LogoProps {
  size?: "sm" | "lg";
  class?: string;
}

/**
 * Habits brand — custom symbol: a streak flame with an embedded checkmark
 * of achievement and a base of three calendar marks (chained days).
 *
 * Drawn with `currentColor` so it ALWAYS contrasts against the background where
 * it is placed. Canonical usage paints it with `text-accent-foreground` over a
 * SOLID chip `bg-accent`: the accent/accent-foreground pair guarantees contrast
 * in any theme (base, gallery, or custom with arbitrary accent). It never relies
 * on loose `text-accent` over a faint surface (that became invisible
 * with low-contrast accents — the bug that broke GymLog).
 */
export function HabitsMark(props: { class?: string }) {
  return (
    <svg
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      class={props.class}
      aria-hidden="true"
    >
      {/* Llama de racha: silueta de crecimiento/constancia */}
      <path
        d="M16 2.6c3.9 4 6.2 7.4 6.2 11.7 0 2-.6 3.7-1.7 5.1 1-.2 2-.7 2.9-1.6.7 1.6 1.1 3.2 1.1 4.7 0 5-4 8.9-8.5 8.9s-8.5-3.9-8.5-8.9c0-3.6 1.9-6.5 3.9-9 .5 1 1.2 1.9 2.2 2.5-.6-4.3 1.2-8.4 4.4-13.4Z"
        fill="currentColor"
      />
      {/* Check de logro embebido (recorte negativo del color del chip) */}
      <path
        d="m11.9 21.4 2.7 2.7 5.4-6"
        stroke="var(--accent, #16a34a)"
        stroke-width="2.6"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  );
}

export function Logo(props: LogoProps) {
  const size = () => props.size ?? "sm";

  return (
    <div
      class={cn(
        "flex items-center gap-2.5 select-none",
        size() === "lg" && "flex-col gap-3 text-center",
        props.class,
      )}
    >
      {size() === "sm" ? (
        <>
          {/* Chip SÓLIDO accent → contraste garantizado para la marca */}
          <span
            data-testid="logo-chip"
            class="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-accent text-accent-foreground shadow-sm shadow-accent/25 ring-1 ring-accent/30"
          >
            <HabitsMark class="h-5 w-5" />
          </span>
          <span class="text-xl font-bold tracking-tight text-foreground">Habits</span>
        </>
      ) : (
        <>
          <span
            data-testid="logo-chip"
            class="mb-1 flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-accent text-accent-foreground shadow-lg shadow-accent/25 ring-1 ring-accent/30 transition-transform duration-300 hover:scale-105 active:scale-95"
          >
            <HabitsMark class="h-9 w-9" />
          </span>
          <h1 class="text-3xl font-bold tracking-tight text-foreground">Habits</h1>
        </>
      )}
    </div>
  );
}
